import React, { useState, useEffect } from "react";
import {
  Card,
  CardContent,
  Typography,
  Button,
  Box,
  Chip,
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from "@mui/material";
import {
  Business as BusinessIcon,
  ColorLens as ColorLensIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  MoreVert as MoreVertIcon,
  CheckCircle as CheckCircleIcon,
  ExitToApp as ExitToAppIcon,
  Add as AddIcon,
  People as PeopleIcon,
} from "@mui/icons-material";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "../contexts/AuthContext";
import { useQuery } from "@tanstack/react-query";
import { organizationApi } from "../services/organization-service";
import { useOrganization } from "../contexts/OrganizationContext";
import { toast } from "sonner";
import OrganizationForm from "./OrganizationForm";
import OrganizationMembers from "./OrganizationMembers";
import {
  clearActiveCheckout,
  readActiveCheckout,
} from "../utils/active-checkout";

const OrganizationList = () => {
  const { t } = useTranslation();
  const {
    currentOrganization,
    organizations,
    loading,
    error,
    initialized,
    setOrganization,
    createOrganization,
    updateOrganization,
    deleteOrganization,
    leaveOrganization,
    loadOrganizations,
  } = useOrganization();
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [formOpen, setFormOpen] = useState(false);
  const [checkoutRestoreReason, setCheckoutRestoreReason] = useState(null);
  const [editingOrg, setEditingOrg] = useState(null);
  const [formLoading, setFormLoading] = useState(false);
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [selectedOrg, setSelectedOrg] = useState(null);
  const {
    data: selectedOrgDetails,
    isPending: menuDetailsPending,
    isFetching: menuDetailsFetching,
    isError: menuDetailsError,
    refetch: retryMenuDetails,
  } = useQuery({
    queryKey: ["organization-menu-details", currentUser?.id, selectedOrg?.id],
    queryFn: async ({ signal }) => {
      const { data } = await organizationApi.getOrganization(selectedOrg.id, {
        signal,
      });
      if (typeof data?.is_inference_only !== "boolean") {
        throw new Error("Organization details are missing the inference-only flag");
      }
      return data;
    },
    enabled: Boolean(menuAnchor && selectedOrg?.id),
    staleTime: 0,
    retry: false,
  });
  const menuDetailsLoading = menuDetailsPending || menuDetailsFetching;
  const [confirmDialog, setConfirmDialog] = useState({
    open: false,
    action: null,
    org: null,
  });
  const [membersDialogOpen, setMembersDialogOpen] = useState(false);

  // Load organizations when component mounts
  useEffect(() => {
    let isMounted = true;

    if (!initialized && isMounted) {
      loadOrganizations();
    }

    // Cleanup function to prevent state updates on unmounted component
    return () => {
      isMounted = false;
    };
  }, [initialized, loadOrganizations]);

  useEffect(() => {
    const checkoutHint = searchParams.get("checkout");
    const paymentHint = searchParams.get("payment");

    if (checkoutHint === "open") {
      setEditingOrg(null);
      setCheckoutRestoreReason(paymentHint);
      setFormOpen(true);
    }

    if (checkoutHint || paymentHint) {
      const nextParams = new URLSearchParams(searchParams);
      nextParams.delete("checkout");
      nextParams.delete("payment");
      setSearchParams(nextParams, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  const handleOpenCreateOrganization = () => {
    const activeCheckout = readActiveCheckout();
    if (activeCheckout?.paymentSubmitted) {
      navigate(
        `/payment/moyasar/callback?session_id=${encodeURIComponent(activeCheckout.sessionId)}`,
      );
      return;
    }

    if (activeCheckout) clearActiveCheckout();

    setEditingOrg(null);
    setCheckoutRestoreReason(null);
    setFormOpen(true);
  };

  const handleCreateOrganization = async (formData) => {
    setFormLoading(true);
    try {
      await createOrganization(formData);
      toast.success(t("organizations.organizationCreatedSuccess"));
      setFormOpen(false);
    } catch (error) {
      console.error("Error creating organization:", error);
      const errorMessage =
        error.response?.data?.error?.message ||
        error.message ||
        t("organizations.failedToCreateOrganization");
      toast.error(errorMessage);
      throw error; // Re-throw to let OrganizationForm handle it
    } finally {
      setFormLoading(false);
    }
  };

  const handleUpdateOrganization = async (formData) => {
    setFormLoading(true);
    try {
      await updateOrganization(editingOrg.id, formData);
      toast.success(t("organizations.organizationUpdatedSuccess"));
      setFormOpen(false);
      setEditingOrg(null);
    } catch (error) {
      console.error("Error updating organization:", error);
      const errorMessage =
        error.response?.data?.detail ||
        error.message ||
        t("organizations.failedToUpdateOrganization");
      toast.error(errorMessage);
      throw error; // Re-throw to let OrganizationForm handle it
    } finally {
      setFormLoading(false);
    }
  };

  const handleDeleteOrganization = async (org) => {
    try {
      await deleteOrganization(org.id);
      toast.success(t("organizations.organizationDeletedSuccess"));
    } catch (error) {
      console.error("Error deleting organization:", error);
      toast.error(
        error.response?.data?.detail ||
          t("organizations.failedToDeleteOrganization"),
      );
    }
  };

  const handleLeaveOrganization = async (org) => {
    try {
      await leaveOrganization(org.id);
      toast.success(t("organizations.leftOrganizationSuccess"));
    } catch (error) {
      console.error("Error leaving organization:", error);
      toast.error(
        error.response?.data?.detail ||
          t("organizations.failedToLeaveOrganization"),
      );
    }
  };

  const handleConfirmAction = (action, org) => {
    setConfirmDialog({ open: true, action, org });
    setMenuAnchor(null);
  };

  const handleConfirmDialogClose = () => {
    setConfirmDialog({ open: false, action: null, org: null });
  };

  const handleConfirmActionExecute = async () => {
    const { action, org } = confirmDialog;

    try {
      if (action === "delete") {
        await handleDeleteOrganization(org);
      } else if (action === "leave") {
        await handleLeaveOrganization(org);
      }
    } catch (error) {
      // Error handling is done in the individual functions
    } finally {
      handleConfirmDialogClose();
    }
  };

  const handleEditClick = (org) => {
    setEditingOrg(org);
    setFormOpen(true);
    setMenuAnchor(null);
  };

  const handleManageMembers = (org) => {
    setSelectedOrg(org);
    setMembersDialogOpen(true);
    setMenuAnchor(null);
  };

  const handleMenuOpen = (event, org) => {
    setMenuAnchor(event.currentTarget);
    setSelectedOrg(org);
  };

  const handleMenuClose = () => {
    setMenuAnchor(null);
    // setSelectedOrg(null);
  };

  const handleSetCurrent = (org) => {
    setOrganization(org);
    toast.success(t("organizations.switchedTo", { name: org.name }));
    handleMenuClose();
  };

  if (loading && !initialized) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  // Show loading state for form operations
  if (formLoading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        {error}
      </Alert>
    );
  }

  return (
    <Box>
      {/* Current Organization Display */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            {t("organizations.currentWorkspace")}
          </Typography>
          {currentOrganization ? (
            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              <BusinessIcon color="primary" />
              <Box sx={{ flexGrow: 1 }}>
                <Typography variant="subtitle1" fontWeight="bold">
                  {currentOrganization.name}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {currentOrganization.description ||
                    t("organizations.noDescription")}
                </Typography>
                <Box
                  sx={{ mt: 1, display: "flex", gap: 1, alignItems: "center" }}
                >
                  <Chip
                    label={t("organizations.active")}
                    color="success"
                    size="small"
                    icon={<CheckCircleIcon />}
                  />
                </Box>
              </Box>
              <Box sx={{ display: "flex", gap: 1 }}>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => handleManageMembers(currentOrganization)}
                >
                  {t("organizations.manageMembers")}
                </Button>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => {
                    setOrganization(null);
                    toast.success(t("organizations.switchedToPersonal"));
                  }}
                >
                  {t("organizations.switchToPersonal")}
                </Button>
              </Box>
            </Box>
          ) : (
            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              <BusinessIcon color="action" />
              <Box sx={{ flexGrow: 1 }}>
                <Typography variant="subtitle1" fontWeight="bold">
                  {t("organizations.personalWorkspace")}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {t("organizations.workingInPersonalSpace")}
                </Typography>
                <Chip
                  label={t("organizations.personal")}
                  color="default"
                  size="small"
                  sx={{ mt: 1 }}
                />
              </Box>
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Organizations List */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 2,
        }}
      >
        <Typography variant="h6">{t("organizations.organizations")}</Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleOpenCreateOrganization}
        >
          {t("organizations.createOrganization")}
        </Button>
      </Box>

      {organizations.length === 0 ? (
        <Card>
          <CardContent sx={{ textAlign: "center", py: 4 }}>
            <BusinessIcon
              sx={{ fontSize: 48, color: "text.secondary", mb: 2 }}
            />
            <Typography variant="h6" color="text.secondary" gutterBottom>
              {t("organizations.noOrganizations")}
            </Typography>
            <Typography variant="body2" color="text.secondary" paragraph>
              {t("organizations.noOrganizationsDescription")}
            </Typography>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleOpenCreateOrganization}
            >
              {t("organizations.createFirstOrganization")}
            </Button>
          </CardContent>
        </Card>
      ) : (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          {organizations.map((org) => (
            <Card key={org.id}>
              <CardContent>
                <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                  <BusinessIcon color="primary" />
                  <Box sx={{ flexGrow: 1 }}>
                    <Typography variant="subtitle1" fontWeight="bold">
                      {org.name}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {org.description || t("organizations.noDescription")}
                    </Typography>
                    <Box
                      sx={{
                        mt: 1,
                        display: "flex",
                        gap: 1,
                        alignItems: "center",
                      }}
                    >
                      <Chip label={org.slug} size="small" variant="outlined" />
                      {currentOrganization?.id === org.id && (
                        <Chip
                          label={t("organizations.current")}
                          color="success"
                          size="small"
                          icon={<CheckCircleIcon />}
                        />
                      )}
                    </Box>
                  </Box>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    {currentOrganization?.id !== org.id && (
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={() => handleSetCurrent(org)}
                      >
                        {t("organizations.switchTo")}
                      </Button>
                    )}
                    <IconButton
                      size="small"
                      onClick={(e) => handleMenuOpen(e, org)}
                    >
                      <MoreVertIcon />
                    </IconButton>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          ))}
        </Box>
      )}

      {/* Organization Form Dialog */}
      <OrganizationForm
        open={formOpen}
        onClose={() => {
          setFormOpen(false);
          setEditingOrg(null);
          setCheckoutRestoreReason(null);
        }}
        onSubmit={
          editingOrg ? handleUpdateOrganization : handleCreateOrganization
        }
        organization={editingOrg}
        loading={formLoading}
        restoreReason={checkoutRestoreReason}
      />

      {/* Organization Menu */}
      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={handleMenuClose}
      >
        {menuDetailsLoading ? (
          <MenuItem disabled>
            <ListItemIcon>
              <CircularProgress size={20} aria-label={t("common.loading")} />
            </ListItemIcon>
            <ListItemText>{t("common.loading")}</ListItemText>
          </MenuItem>
        ) : menuDetailsError ? (
          <MenuItem onClick={() => retryMenuDetails()}>
            <ListItemText
              primary={t("organizations.failedToLoadDetails")}
              secondary={t("common.retry")}
            />
          </MenuItem>
        ) : [
            selectedOrg?.role_id === "owner" && selectedOrgDetails?.is_inference_only === false ? (
              <MenuItem key="theme" onClick={() => navigate(`/update/theme/${selectedOrg.id}`)}>
                <ListItemIcon>
                  <ColorLensIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>{t("organizations.updateTheme")}</ListItemText>
              </MenuItem>
            ) : null,
            <MenuItem key="edit" onClick={() => handleEditClick(selectedOrg)}>
              <ListItemIcon>
                <EditIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>{t("organizations.editOrganization")}</ListItemText>
            </MenuItem>,
            <MenuItem key="members" onClick={() => handleManageMembers(selectedOrg)}>
              <ListItemIcon>
                <PeopleIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>{t("organizations.manageMembers")}</ListItemText>
            </MenuItem>,
            <Divider key="divider" />,
            <MenuItem
              key="leave"
              onClick={() => handleConfirmAction("leave", selectedOrg)}
              sx={{ color: "warning.main" }}
            >
              <ListItemIcon>
                <ExitToAppIcon fontSize="small" color="warning" />
              </ListItemIcon>
              <ListItemText>{t("organizations.leaveOrganization")}</ListItemText>
            </MenuItem>,
            <MenuItem
              key="delete"
              onClick={() => handleConfirmAction("delete", selectedOrg)}
              sx={{ color: "error.main" }}
            >
              <ListItemIcon>
                <DeleteIcon fontSize="small" color="error" />
              </ListItemIcon>
              <ListItemText>{t("organizations.deleteOrganization")}</ListItemText>
            </MenuItem>,
        ]}
      </Menu>

      {/* Confirmation Dialog */}
      <Dialog open={confirmDialog.open} onClose={handleConfirmDialogClose}>
        <DialogTitle>
          {confirmDialog.action === "delete"
            ? t("organizations.deleteOrganization")
            : t("organizations.leaveOrganization")}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {confirmDialog.action === "delete"
              ? t("organizations.deleteOrgConfirm", {
                  name: confirmDialog.org?.name,
                })
              : t("organizations.leaveOrgConfirm", {
                  name: confirmDialog.org?.name,
                })}
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button
            onClick={handleConfirmDialogClose}
            color="secondary"
            variant="text"
          >
            {t("common.cancel")}
          </Button>
          <Button
            onClick={handleConfirmActionExecute}
            color="error"
            variant="contained"
          >
            {confirmDialog.action === "delete"
              ? t("common.delete")
              : t("organizations.leave")}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Organization Members Dialog */}
      <OrganizationMembers
        open={membersDialogOpen}
        onClose={() => setMembersDialogOpen(false)}
        organization={selectedOrg}
      />
    </Box>
  );
};

export default OrganizationList;
